import React, { useEffect, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

const NetBalanceChart = ({ years, currency, selectedGlobalYear }) => {
  const history = useHistory();
  const [chartData, setChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(selectedGlobalYear || new Date().getFullYear());
  useEffect(() => {
    // Check if user is not logged in (localStorage is empty)
    if (!localStorage.getItem("userData")) {
      // Redirect to the login page
      history.push("/login");
    }
  }, [history]);

  //get userid form loca storage
  const storedUserData = localStorage.getItem("userData");
  let userId = "";
  let groupId = "";
  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    userId = userData.userId;
    groupId = userData.groupId;
  }

  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    setSelectedYear(selectedGlobalYear); // Sync with global year when it changes
  }, [selectedGlobalYear]);

  // Fetch chart data
  useEffect(() => {
    if (!groupId) return;
    const fetchData = async () => {
      try {
        const data = await apiService.getNetBalance(groupId, selectedYear);
        setChartData(data);
      } catch (err) {
        console.error("Error fetching net balance:", err);
      }
    };
    fetchData();
  }, [selectedYear, groupId]);

  // Detect dark mode
  useEffect(() => {
    const checkDarkMode = () =>
      document.documentElement.classList.contains("dark");
    setIsDarkMode(checkDarkMode());

    const observer = new MutationObserver(() => {
      setIsDarkMode(checkDarkMode());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const textColor = isDarkMode ? "#ffffff" : "#1f2937";
  const borderColor = isDarkMode ? "#4B5563" : "#E5E7EB";
  const backgroundColor = isDarkMode ? "#1F2937" : "#ffffff";
  const gridLineColor = isDarkMode ? "#374151" : "#D1D5DB";
  const fillTop = isDarkMode ? "#2CAFFE" : "#3a86ff";
  const fillBottom = isDarkMode ? "#3a86ff" : "#2CAFFE";

  const now = new Date();
  const firstDayOfYear = new Date(selectedYear, 0, 1).getTime();
  const lastDayOfCurrentMonth = new Date(selectedYear, now.getMonth() + 1, 0).getTime();

  const options = {
    chart: {
      type: "areaspline",
      height: 500,
      backgroundColor,
      spacing: [20, 10, 30, 10],
      style: { fontFamily: "Inter, sans-serif" },
    },
    title: { text: undefined },
    xAxis: {
      type: "datetime",
      min: firstDayOfYear,
      max: lastDayOfCurrentMonth,
      lineColor: borderColor,
      tickColor: borderColor,
      labels: { style: { color: textColor } },
    },
    yAxis: {
      opposite: false,
      title: { text: `${currency} (Balance)`, style: { color: textColor } },
      labels: {
        formatter: function () {
          const value = this.value;
          if (Math.abs(value) >= 100000) return `${currency}${(value / 100000).toFixed(1)}L`;
          if (Math.abs(value) >= 1000) return `${currency}${(value / 1000).toFixed(1)}K`;
          return currency + value;
        },
        style: { color: textColor },
        align: "right",
        x: -10,
      },
      gridLineColor,
      tickPixelInterval: 50,
    },
    tooltip: {
      shared: true,
      xDateFormat: "%A, %b %e, %Y",
      valuePrefix: `${currency}`,
      valueDecimals: 0,
      backgroundColor: isDarkMode ? "#111827" : "#ffffff",
      style: { color: textColor },
    },
    rangeSelector: {
      selected: 2,
      buttons: [
        { type: "month", count: 1, text: "1m" },
        { type: "month", count: 3, text: "3m" },
        { type: "month", count: 6, text: "6m" },
        { type: "year", count: 1, text: "1y" },
        { type: "all", text: "All" },
      ],
      inputEnabled: false,
      buttonTheme: {
        fill: isDarkMode ? "#374151" : "#f9fafb",
        style: { color: textColor },
        states: {
          hover: { fill: isDarkMode ? "#4B5563" : "#e5e7eb" },
          select: { fill: "#3b82f6", style: { color: "#ffffff" } },
        },
      },
      labelStyle: { color: textColor },
    },
    series: [
      {
        type: "areaspline",
        name: "Net Balance",
        data: chartData,
        color: "#3b82f6",
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, fillTop],
            [0.5, "rgba(59,130,246,0.25)"],
            [1, fillBottom],
          ],
        },
        tooltip: { valueDecimals: 0 },
        zones: [
          {
            value: 0,
            color: "#cc3a3a",
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, "#cc3a3a"],
                [0.5, "rgba(239, 68, 68, 0.25)"],
                [1, "#ef4444"],
              ],
            },
          },
          {
            color: "#2bac5e",
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [
                [0, "#2bac5e"],
                [0.5, "#4ade80"],
                [1, "#64ff9da3"],
              ],
            },
          },
        ],
      },
    ],
    credits: { enabled: false },
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-4 col-span-full">
      <div className="flex justify-between items-center mb-2 flex-col lg:flex-row">
        <h2 className="text-lg text-gray-800 dark:text-white font-semibold">
          Net Balance ({currency}) - Week-wise ({selectedYear})
        </h2>
        <select
          className="bg-gray-100 dark:bg-gray-700 border dark:border-gray-600 text-xs text-gray-800 dark:text-white rounded-md px-3 py-2 mt-2 lg:mt-0"
          style={{ minWidth: "80px", border: "solid #ddd 1px" }}
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
      <div className="h-[500px]">
        {chartData.length > 0 ? (
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="stockChart"
            options={options}
          />
        ) : (
          <div className="w-full h-full animate-pulse bg-gray-200 dark:bg-gray-700 rounded-md" />
        )}
      </div>
    </div>
  );
};

export default NetBalanceChart;
